import { computed, Injectable, signal } from '@angular/core';

export type LoadingOverlayOptions = {
  title?: string;
  message?: string;
};

export type LoadingOverlayState = {
  visible: boolean;
  title: string;
  message: string;
  count: number;
};

export type LoadingOverlayHandle = {
  close: () => void;
};

type LoadingOverlayEntry = Required<LoadingOverlayOptions> & {
  id: number;
};

const DEFAULT_LOADING_OVERLAY_STATE: Required<LoadingOverlayOptions> = {
  title: 'Loading workspace',
  message: 'Preparing portal content',
};

@Injectable({ providedIn: 'root' })
export class LoadingOverlayService {
  private readonly entries = signal<LoadingOverlayEntry[]>([]);
  private readonly nextId = signal(0);

  readonly state = computed<LoadingOverlayState>(() => {
    const entries = this.entries();
    const current = entries[entries.length - 1] ?? null;

    return {
      visible: entries.length > 0,
      title: current?.title ?? DEFAULT_LOADING_OVERLAY_STATE.title,
      message: current?.message ?? DEFAULT_LOADING_OVERLAY_STATE.message,
      count: entries.length,
    };
  });

  show(options: LoadingOverlayOptions = {}): LoadingOverlayHandle {
    const entry: LoadingOverlayEntry = {
      id: this.nextId() + 1,
      title: options.title?.trim() || DEFAULT_LOADING_OVERLAY_STATE.title,
      message: options.message?.trim() || DEFAULT_LOADING_OVERLAY_STATE.message,
    };

    this.nextId.set(entry.id);
    this.entries.update((entries) => [...entries, entry]);

    return {
      close: () => this.hide(entry.id),
    };
  }

  hide(handleId: number): void {
    this.entries.update((entries) => entries.filter((entry) => entry.id !== handleId));
  }

  clear(): void {
    this.entries.set([]);
  }
}
